import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
    arrow,
    autoUpdate,
    flip,
    offset,
    type Placement,
    useDismiss,
    useFloating,
    useFocus,
    useHover,
    useInteractions,
    safePolygon
} from '@floating-ui/react';
import { useDefaultId } from 'app/lib/hooks';
import { type ReferenceElement, resolveReferenceElement } from 'app/lib/utils';
import { Tooltip } from './Tooltip';

const ARROW_HEIGHT = 7;
const GAP = 2;

export interface UseAccessibleTooltipParams {
    placement?: Placement;
    reference?: ReferenceElement;
    autoOpenDurationMs?: number;
    keepTooltipOpen?: boolean;
}

export interface ShowTooltipParams {
    duration: number;
}

export function useAccessibleTooltip(params: UseAccessibleTooltipParams = {}) {
    const { placement, reference, autoOpenDurationMs, keepTooltipOpen } = params;
    const id = useDefaultId('tooltip');
    const arrowRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [referenceElement, setReferenceElement] = useState(resolveReferenceElement(reference));

    useLayoutEffect(() => {
        setReferenceElement(resolveReferenceElement(reference));
    }, [reference]);

    const floatingUiParams = {
        elements: {
            reference: referenceElement
        },
        placement,
        open: isOpen,
        onOpenChange: setIsOpen,
        whileElementsMounted: autoUpdate,
        middleware: [offset(ARROW_HEIGHT + GAP), flip(), arrow({ element: arrowRef })]
    };

    const { refs, elements, floatingStyles, context } = useFloating(floatingUiParams);

    const hover = useHover(context, {
        restMs: 250,
        delay: { open: 1000 },
        handleClose: keepTooltipOpen ? safePolygon() : null
    });
    const focus = useFocus(context);
    const dismiss = useDismiss(context);
    const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss]);

    const showTooltip = useCallback(({ duration }: ShowTooltipParams) => {
        setIsOpen(true);

        if (duration) {
            setTimeout(() => {
                setIsOpen(false);
            }, duration);
        }
    }, []);

    useEffect(() => {
        if (autoOpenDurationMs) {
            showTooltip({ duration: autoOpenDurationMs });
        }
    }, [showTooltip, autoOpenDurationMs]);

    useEffect(() => {
        try {
            elements.floating?.togglePopover(isOpen);
        } catch {
            // Ignore error. We might be trying to call this on an element
            // that's being removed from the DOM.
        }
    }, [elements.floating, isOpen]);

    return {
        Tooltip,
        refs,
        showTooltip,
        getReferenceProps: (props = {}) =>
            getReferenceProps({
                'aria-describedby': id,
                ...(referenceElement ? {} : { ref: refs.setReference }),
                ...props
            }),
        getTooltipProps: (props = {}) => ({
            arrowRef,
            context,
            ...getFloatingProps({
                ref: refs.setFloating,
                style: {
                    ...floatingStyles,
                    pointerEvents: 'auto'
                },
                id,
                ...props
            })
        })
    };
}
